import React from 'react';
import { DeckImage, DeckItem } from '../types';
import { fill, inlineMarks, longtextBlocks } from '../text';
import { EditableText } from './EditableText';
import { img as readImg, items as readItems, str, useSlide } from './context';

/** A font size that follows the nearest Fit box's shrink factor. */
export const fs = (px: number): string => `calc(${px}px * var(--fit, 1))`;

type Tag = keyof JSX.IntrinsicElements;
interface TextProps { as?: Tag; className?: string; style?: React.CSSProperties; placeholder?: string; max?: number; fallback?: string }

// #region Text
/** A plain text field by key. View: filled, with **bold** / *italic*; empty renders nothing. Edit: in place. */
export const Text: React.FunctionComponent<TextProps & { k: string }> = ({ k, ...rest }): React.ReactElement | null => {
    const { slide, onFieldChange } = useSlide();
    const raw = str(slide.fields[k]);
    return <RawText raw={raw} fieldKey={k} onChange={(v) => onFieldChange?.(k, v)} {...rest} />;
};

// #region RawText
export const RawText: React.FunctionComponent<TextProps & { raw: string; fieldKey: string; onChange: (v: string) => void; multiline?: boolean }> = ({
    raw, fieldKey, onChange, as = 'div', className, style, placeholder, max, fallback, multiline
}): React.ReactElement | null => {
    const { context, mode, edit, onSelectField } = useSlide();
    if (edit) {
        return <EditableText as={as} value={raw} values={context.values} onChange={onChange} onFocus={() => onSelectField?.(fieldKey)}
            placeholder={placeholder} max={max} className={className} style={style} multiline={multiline} />;
    }
    const filled = fill(raw || fallback || '', context.values, mode).trim();
    if (!filled) return null;
    return React.createElement(as, { className, style }, multiline ? <Blocks text={filled} /> : inlineMarks(filled));
};

// #region LongText
/** A longtext field: paragraphs and "- " bullets in view; one multiline editable in edit. */
export const LongText: React.FunctionComponent<TextProps & { k: string }> = ({ k, ...rest }): React.ReactElement | null => {
    const { slide, onFieldChange } = useSlide();
    return <RawText raw={str(slide.fields[k])} fieldKey={k} onChange={(v) => onFieldChange?.(k, v)} multiline {...rest} />;
};

// #region Blocks
export const Blocks: React.FunctionComponent<{ text: string }> = ({ text }): React.ReactElement => (
    <>
        {longtextBlocks(text).map((b, i) => (b.kind === 'p'
            ? <p key={i} className="wd-para">{inlineMarks(b.text)}</p>
            : <ul key={i} className="wd-list">{b.items.map((t, j) => <li key={j}>{inlineMarks(t)}</li>)}</ul>))}
    </>
);

// #region ItemText
/** One text part (title, body, value, label) of one item in an items field; edits send the whole new array. */
export const ItemText: React.FunctionComponent<TextProps & { k?: string; index: number; part: 'title' | 'body' | 'value' | 'label'; multiline?: boolean }> = ({
    k = 'items', index, part, ...rest
}): React.ReactElement | null => {
    const { slide, onFieldChange } = useSlide();
    const list = readItems(slide.fields[k]);
    const raw = str(list[index]?.[part]);
    const change = (v: string): void => {
        const next: DeckItem[] = list.map((it, i) => (i === index ? { ...it, [part]: v } : it));
        onFieldChange?.(k, next);
    };
    return <RawText raw={raw} fieldKey={`${k}.${index}.${part}`} onChange={change} {...rest} />;
};

// #region ImageSlot
interface SlotProps {
    fieldKey: string;
    image: DeckImage | null;
    className?: string;
    style?: React.CSSProperties;
    /** Adds the theme's gradient frame when the theme has one. */
    framed?: boolean;
    round?: boolean;
    fit?: 'cover' | 'contain';
    label?: string;
}

/** An image field. View: the image (nothing when empty). Edit: click to pick; empty shows a faint drop zone. */
export const ImageSlot: React.FunctionComponent<SlotProps> = ({ fieldKey, image, className, style, framed, round, fit = 'cover', label = 'Add image' }): React.ReactElement | null => {
    const { edit, context, onSelectField, renderImageSlot } = useSlide();
    if (!edit && !image) return null;
    const custom = edit && renderImageSlot ? renderImageSlot(fieldKey, image) : undefined;
    const frame = framed && !!context.theme.frame;
    const cls = ['wd-img', frame ? 'wd-framed' : '', round ? 'wd-round' : '', !image ? 'wd-img-empty' : '', edit ? 'wd-img-edit' : '', className ?? ''].filter(Boolean).join(' ');
    const inner = custom !== undefined ? custom : image
        ? <img src={image.url} alt={image.alt} style={{ objectFit: fit }} draggable={false} />
        : <span className="wd-img-hint"><svg viewBox="0 0 24 24" width="56" height="56" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="4" width="18" height="16" rx="2.5" /><circle cx="9" cy="10" r="2" /><path d="M21 16l-5.5-5.5L5 20" /></svg><span>{label}</span></span>;
    if (!edit) return <div className={cls} style={style}>{inner}</div>;
    return (
        <div className={cls} style={style} role="button" tabIndex={0} aria-label={image ? `Change image: ${image.alt || fieldKey}` : label}
            onClick={(e) => { e.stopPropagation(); onSelectField?.(fieldKey); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectField?.(fieldKey); } }}>
            {inner}
        </div>
    );
};

// #region FieldImage
export const FieldImage: React.FunctionComponent<Omit<SlotProps, 'image' | 'fieldKey'> & { k: string }> = ({ k, ...rest }): React.ReactElement | null => {
    const { slide } = useSlide();
    return <ImageSlot fieldKey={k} image={readImg(slide.fields[k])} {...rest} />;
};

const LEVELS = [1, 0.92, 0.85, 0.78, 0.72, 0.66];
const overflows = (el: HTMLElement): boolean => el.scrollHeight > el.clientHeight + 2 || el.scrollWidth > el.clientWidth + 2;

// #region Fit
/**
 * A box that shrinks the type inside it (through --fit, read by fs()) a step at a time until nothing overflows.
 * Sizes are layout sizes, so the result is the same at any CSS scale: editor, viewer and PDF agree.
 * Past the smallest size it adds `wd-fit-clamp`, which clamps `.wd-clamp` text (item bodies) to a few lines.
 * Boxes inside that clip their own content (cards in a fixed-height grid) opt in to the check with class `wd-fit-watch`.
 */
export const Fit: React.FunctionComponent<React.PropsWithChildren<{ className?: string; style?: React.CSSProperties; sig?: string }>> = ({ className, style, sig, children }): React.ReactElement => {
    const { sig: slideSig, edit } = useSlide();
    // One step past the smallest size: clamp long item bodies (view and print only; edit mode shows every word).
    const top = edit ? LEVELS.length - 1 : LEVELS.length;
    const ref = React.useRef<HTMLDivElement>(null);
    const [level, setLevel] = React.useState(0);
    const [fontsTick, setFontsTick] = React.useState(0);
    React.useEffect(() => {
        const fonts = (document as any).fonts;
        if (!fonts?.addEventListener) return;
        const bump = (): void => setFontsTick((n) => n + 1);
        fonts.addEventListener('loadingdone', bump);
        return () => fonts.removeEventListener('loadingdone', bump);
    }, []);
    const key = `${sig ?? slideSig}|${fontsTick}`;
    const last = React.useRef(key);
    React.useLayoutEffect(() => {
        if (last.current !== key) {
            last.current = key;
            if (level !== 0) { setLevel(0); return; }
        }
        const el = ref.current;
        if (el && level < top && (overflows(el) || Array.from(el.querySelectorAll<HTMLElement>('.wd-fit-watch')).some(overflows))) setLevel(level + 1);
    });
    const cls = `wd-fit${level >= LEVELS.length ? ' wd-fit-clamp' : ''}${className ? ` ${className}` : ''}`;
    return <div ref={ref} className={cls} style={{ ...style, '--fit': LEVELS[Math.min(level, LEVELS.length - 1)] } as React.CSSProperties}>{children}</div>;
};
